import React from "react";
import { useIntl } from "react-intl";
import {
  Link,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router-dom";

import { ReactComponent as MoveSVG } from "../../assets/arrow-line-right-thin.svg";
import { ReactComponent as DeleteSVG } from "../../assets/close-thin.svg";
import { useDeviceGroupStore } from "../../store/device-group/DeviceGroupStore";

import Button from "../../components/button/Button";
import Popup, { PopupContext } from "../../components/popup/Popup";

import { useDialog } from "../../components/dialog/useDialog";
import useErrorAlert from "../../hooks/useErrorAlert";
import { useLoading } from "../../store/loading/LoadingContextStore";

import DeviceInterface from "../../interfaces/DeviceInterface";
import CustomError from "../../service/errors/custom.error";
import { MainPageOutletContext } from "../main/Main";
import "./device.scss";

function SettingControls({
  device,
  deleteDevice,
}: {
  device: DeviceInterface;
  deleteDevice: Function;
}) {
  const { formatMessage } = useIntl();
  const showErrorAlert = useErrorAlert();
  const { close } = React.useContext(PopupContext);
  const outlet = useOutletContext<MainPageOutletContext>();

  return (
    <>
      <Button
        Component={Link}
        title={formatMessage({
          id:
            device?.groupId == null
              ? "device.move.title"
              : "device.move_to_group",
        })}
        to={outlet.generatePath("/device/:deviceType/:deviceId/move", {
          deviceId: device.id,
          deviceType: device.type,
        })}
        Icon={MoveSVG}
      />

      {device.groupId != null && (
        <Button
          buttonType="delete"
          title={formatMessage({ id: "device.remove" })}
          Icon={DeleteSVG}
          onClick={async () => {
            try {
              if (await deleteDevice()) {
                close();
              }
            } catch (e) {
              console.error("Error when remove a device group", e);
              await showErrorAlert(
                e instanceof CustomError ? e.getStatusText() : undefined
              );
            }
          }}
        />
      )}
    </>
  );
}

function DeviceSettingsPopup() {
  const navigate = useNavigate();
  const outlet = useOutletContext<MainPageOutletContext>();

  const { showConfirm } = useDialog();
  const { formatMessage } = useIntl();
  const { saveDeviceGroup, findDeviceById } = useDeviceGroupStore();
  const processingAsyncFn = useLoading();

  const { deviceId, deviceType } = useParams();
  const device = deviceId ? findDeviceById(deviceId, deviceType) : undefined;

  const deleteDevice = React.useCallback(async () => {
    if (device?.id == null || device?.type == null) return undefined;

    if (
      await showConfirm({
        title: formatMessage({ id: "device.remove.confirm_title" }),
        text: formatMessage({ id: "device.remove.confirm_text" }),
        confirmLabel: formatMessage({ id: "device.remove.confirm_yes" }),
      })
    ) {
      await processingAsyncFn(() =>
        saveDeviceGroup(device.id, device.type, undefined)
      );
      return true;
    }

    return false;
  }, [
    showConfirm,
    formatMessage,
    processingAsyncFn,
    saveDeviceGroup,
    device?.id,
    device?.type,
  ]);

  if (deviceId != null && device == null) return null;

  return (
    <Popup
      title="booster Settings"
      className="device-settings-popup"
      close={() => navigate(outlet.baseUrl)}
    >
      <SettingControls device={device} deleteDevice={deleteDevice} />
    </Popup>
  );
}

export default DeviceSettingsPopup;
